import type {
  Operator,
  Stop,
  PlaceSearchParams,
  PlaceSearchResult,
  StopDetail,
  StopDepartures,
  Line,
  Disruption,
  JourneyPlanPageLink,
  JourneyPlanRequest,
  JourneyPlanResponse,
  JourneyPlan,
  Timetable,
  LineShape,
  LineAssets,
  StopSearchParams,
} from './types';
import {
  fetchJson,
  buildUrl,
  ApiError,
  type RequestOptions,
  type ParamValue,
} from './fetcher';
import type {
  HalDisruptionsResponse,
  HalLine,
  HalLineAssetsResponse,
  HalLinesResponse,
  HalOperatorsResponse,
  HalPlanLink,
  HalPlacesResponse,
  HalPlanResponse,
  HalStopDetailResponse,
  HalStopsResponse,
  HalTimetableResponse,
  HalVisitsResponse,
} from './client/hal';
import {
  extractEmbeddedDisruptions,
  mapDisruptions,
  mapHrefLinks,
  mapLeg,
  mapLine,
  mapLineAssetsResponse,
  mapLineRefs,
  mapPlaceSearchResult,
  mapStop,
  mapTimetableResponse,
  mapVisit,
} from './client/mappers';

const ACCEPT_HEADER = 'application/hal+json, application/json';

const LAT_LNG_PATTERN = /^-?(?:\d+\.?\d*|\d*\.\d+),-?(?:\d+\.?\d*|\d*\.\d+)$/;

// Matches absolute URLs, including protocol-relative ones (`//host/path`).
const ABSOLUTE_URL_PATTERN = /^([a-z][a-z0-9+.-]*:)?\/\//i;

function resolveDefaultBaseUrl(moduleUrl: string): string {
  try {
    const parsed = new URL(moduleUrl);

    if (!parsed.hostname) {
      return '';
    }

    const protocol =
      parsed.protocol === 'http:' || parsed.protocol === 'https:'
        ? parsed.protocol
        : 'https:';
    const port = parsed.port ? `:${parsed.port}` : '';

    return `${protocol}//${parsed.hostname}${port}`;
  } catch {
    return '';
  }
}

const DEFAULT_BASE_URL = resolveDefaultBaseUrl(import.meta.url);

// Query param carrying the host of the page embedding the widget. Line-asset
// documents are opened as top-level browser navigations, which send no Origin
// or Referer, so the BFF reads the host from here instead to attribute the
// request to a partner. Keep in sync with functions/api/validation.ts.
const WIDGET_HOST_PARAM = 'widgetHost';

function resolveEmbeddingHost(): string {
  return typeof window === 'undefined' ? '' : (window.location?.host ?? '');
}

// ---------------------------------------------------------------------------
// Client
// ---------------------------------------------------------------------------

export interface ApiClientConfig {
  tenant: string;
  baseUrl?: string;
  timeout?: number;
  headers?: Record<string, string>;
  /** Host of the embedding page; defaults to the current browser location. */
  embeddingHost?: string;
}

export interface ApiRequestDetails {
  method: 'GET';
  url: string;
  headers: Record<string, string>;
  timeout: number;
}

export { ApiError };
export type { RequestOptions };

export class ArcticApiClient {
  private readonly baseUrl: string;
  private readonly tenant: string;
  private readonly timeout: number;
  private readonly defaultParams: Record<string, string>;
  private readonly headers: Record<string, string>;
  private readonly embeddingHost: string;

  constructor({
    tenant,
    baseUrl = DEFAULT_BASE_URL,
    timeout = 30_000,
    headers = {},
    embeddingHost = resolveEmbeddingHost(),
  }: ApiClientConfig) {
    if (!tenant) throw new Error('tenant is required');
    this.tenant = tenant;
    this.baseUrl = baseUrl;
    this.timeout = timeout;
    this.defaultParams = { platform_source: 'widget' };
    this.headers = headers;
    this.embeddingHost = embeddingHost;
  }

  private url(path: string, params?: Record<string, ParamValue>): string {
    return buildUrl(this.baseUrl, `/api/${this.tenant}/${path}`, {
      ...this.defaultParams,
      ...params,
    });
  }

  private buildRequest(
    url: string,
    options?: RequestOptions,
  ): ApiRequestDetails {
    return {
      method: 'GET',
      url,
      headers: {
        Accept: ACCEPT_HEADER,
        ...this.headers,
        ...options?.headers,
      },
      timeout: options?.timeout ?? this.timeout,
    };
  }

  private hrefUrl(
    href: string,
    extraParams?: (params: URLSearchParams) => void,
  ): string {
    // HAL hrefs are usually paths relative to the API root; anything already
    // absolute (eg a direct document URL) is passed through untouched.
    if (ABSOLUTE_URL_PATTERN.test(href)) {
      return href;
    }

    const qIndex = href.indexOf('?');
    const path = qIndex >= 0 ? href.slice(0, qIndex) : href;
    const existingParams = qIndex >= 0 ? href.slice(qIndex + 1) : '';
    const params = new URLSearchParams(existingParams);
    extraParams?.(params);
    for (const [k, v] of Object.entries(this.defaultParams)) {
      if (!params.has(k)) params.set(k, v);
    }
    const paramStr = params.toString();
    return `${this.baseUrl}/api/${this.tenant}${path}${paramStr ? '?' + paramStr : ''}`;
  }

  /**
   * Resolve the href of an individual line-asset document (a PDF timetable,
   * map or fares sheet). These are opened as top-level browser navigations
   * that send no Origin or Referer, so the embedding host is passed along for
   * the BFF to attribute the request to a partner.
   */
  private lineAssetHrefUrl(href: string): string {
    return this.hrefUrl(href, (params) => {
      if (this.embeddingHost) {
        params.set(WIDGET_HOST_PARAM, this.embeddingHost);
      }
    });
  }

  private disruptionsHrefUrl(href: string): string {
    const qIndex = href.indexOf('?');
    const path = qIndex >= 0 ? href.slice(0, qIndex) : href;
    const existingParams = qIndex >= 0 ? href.slice(qIndex + 1) : '';
    const params = new URLSearchParams(existingParams);

    if (/^\/network\/stops\/[^/]+\/alerts$/u.test(path)) {
      const directive = 'includeFromLinesServed';
      const key = 'directive[]';

      if (!params.getAll(key).includes(directive)) {
        params.append(key, directive);
      }
    }

    for (const [k, v] of Object.entries(this.defaultParams)) {
      if (!params.has(k)) params.set(k, v);
    }

    const paramStr = params.toString();
    return `${this.baseUrl}/api/${this.tenant}${path}${paramStr ? '?' + paramStr : ''}`;
  }

  private stopSearchUrl(params: StopSearchParams | string): string | null {
    const queryParams: Record<string, ParamValue> = {};

    if (typeof params === 'string') {
      if (!params.trim()) return null;
      queryParams.search = params;
    } else {
      const trimmedQuery = params.query?.trim() ?? '';

      if (trimmedQuery.length > 0) {
        queryParams.search = trimmedQuery;
      }

      if (params.bounds) {
        if (params.bounds.length !== 2) {
          throw new Error(
            'Stop search bounds must contain exactly two points.',
          );
        }

        for (const point of params.bounds) {
          if (!LAT_LNG_PATTERN.test(point)) {
            throw new Error(
              'Stop search bounds must use `latitude,longitude` format.',
            );
          }
        }

        queryParams['bounds[]'] = params.bounds;
      }

      if (!queryParams.search && !queryParams['bounds[]']) {
        return null;
      }
    }

    return this.url('network/stops', queryParams);
  }

  private searchLinesUrl(query: string): string | null {
    const trimmed = query.trim();

    if (!trimmed) {
      return null;
    }

    return this.url('network/lines', {
      search: trimmed,
      'include[]': 'alerts',
    });
  }

  private placeSearchUrl(params: PlaceSearchParams | string): string | null {
    const queryParams: Record<string, ParamValue> = {};

    if (typeof params === 'string') {
      const trimmedQuery = params.trim();

      if (!trimmedQuery) {
        return null;
      }

      queryParams.search = trimmedQuery;
    } else {
      const trimmedQuery = params.query.trim();

      if (!trimmedQuery) {
        return null;
      }

      queryParams.search = trimmedQuery;

      if (params.near) {
        if (!LAT_LNG_PATTERN.test(params.near)) {
          throw new Error(
            'Place search near must use `latitude,longitude` format.',
          );
        }

        queryParams.near = params.near;
      }
    }

    return this.url('places', queryParams);
  }

  private lineUrl(identifier: string): string | null {
    const trimmed = identifier.trim();

    if (!trimmed) {
      return null;
    }

    if (/^https?:\/\//i.test(trimmed)) {
      const url = new URL(trimmed);
      return this.lineUrl(url.pathname);
    }

    if (trimmed.startsWith('/network/lines/')) {
      const [pathPart] = trimmed.split('?');
      const normalizedPath = pathPart
        .replace(/\/(?:alerts|assets|changes|shapes|stops)$/u, '')
        .replace(/\/timetables$/u, '');

      return this.url(normalizedPath.replace(/^\//u, ''), {
        'include[]': 'alerts',
      });
    }

    return this.url(`network/lines/${encodeURIComponent(trimmed)}`, {
      'include[]': 'alerts',
    });
  }

  private journeyPlanUrl(params: JourneyPlanRequest): string {
    if (!LAT_LNG_PATTERN.test(params.origin)) {
      throw new Error('planJourney origin must be a lat,lng coordinate string');
    }

    if (!LAT_LNG_PATTERN.test(params.destination)) {
      throw new Error(
        'planJourney destination must be a lat,lng coordinate string',
      );
    }

    const queryParams: Record<string, ParamValue> = {
      origin: params.origin,
      destination: params.destination,
    };

    if (params.originName) queryParams.originName = params.originName;
    if (params.destinationName) {
      queryParams.destinationName = params.destinationName;
    }

    if (params.arriveBy) queryParams.arriveBy = params.arriveBy;
    if (params.leaveAfter) queryParams.leaveAfter = params.leaveAfter;

    if (params.options) {
      const opts = params.options;
      if (opts.mode) {
        const modes = Array.isArray(opts.mode) ? opts.mode : [opts.mode];

        if (modes.length === 1) {
          queryParams['options[mode]'] = modes[0];
        } else if (modes.length > 1) {
          queryParams['options[mode][]'] = modes;
        }
      }
      if (opts.maxWalkDistance !== undefined) {
        queryParams['options[maxWalkDistance]'] = String(opts.maxWalkDistance);
      }
      if (opts.walkSpeed !== undefined) {
        queryParams['options[walkSpeed]'] = String(opts.walkSpeed);
      }
      if (opts.minimiseWalking !== undefined) {
        queryParams['options[minimiseWalking]'] = String(opts.minimiseWalking);
      }
    }

    return this.url('plan', queryParams);
  }

  private fetch<T>(url: string, options?: RequestOptions): Promise<T> {
    const request = this.buildRequest(url, options);
    return fetchJson<T>(
      request.url,
      {
        signal: options?.signal,
        timeout: request.timeout,
        headers: request.headers,
      },
      request.timeout,
    );
  }

  async fetchRawResponse(request: ApiRequestDetails): Promise<unknown> {
    return fetchJson<unknown>(
      request.url,
      {
        timeout: request.timeout,
        headers: request.headers,
      },
      request.timeout,
    );
  }

  listOperatorsRequest(options?: RequestOptions): ApiRequestDetails {
    return this.buildRequest(this.url('network/operators'), options);
  }

  searchStopsRequest(
    params: StopSearchParams | string,
    options?: RequestOptions,
  ): ApiRequestDetails | null {
    const url = this.stopSearchUrl(params);
    return url ? this.buildRequest(url, options) : null;
  }

  getStopRequest(
    atcoCode: string,
    options?: RequestOptions,
  ): ApiRequestDetails {
    return this.buildRequest(
      this.url(`network/stops/${encodeURIComponent(atcoCode)}`),
      options,
    );
  }

  getStopDeparturesRequest(
    href: string,
    options?: RequestOptions,
  ): ApiRequestDetails {
    return this.buildRequest(this.hrefUrl(href), options);
  }

  getDisruptionsRequest(
    href: string,
    options?: RequestOptions,
  ): ApiRequestDetails {
    return this.buildRequest(this.disruptionsHrefUrl(href), options);
  }

  getOperatorLinesRequest(
    operator: string,
    options?: RequestOptions,
  ): ApiRequestDetails {
    return this.buildRequest(
      this.url(`network/operators/${encodeURIComponent(operator)}/lines`, {
        'include[]': 'alerts',
      }),
      options,
    );
  }

  searchLinesRequest(
    query: string,
    options?: RequestOptions,
  ): ApiRequestDetails | null {
    const url = this.searchLinesUrl(query);
    return url ? this.buildRequest(url, options) : null;
  }

  searchPlacesRequest(
    params: PlaceSearchParams | string,
    options?: RequestOptions,
  ): ApiRequestDetails | null {
    const url = this.placeSearchUrl(params);
    return url ? this.buildRequest(url, options) : null;
  }

  getTimetableRequest(
    href: string,
    options?: RequestOptions,
  ): ApiRequestDetails {
    return this.buildRequest(this.hrefUrl(href), options);
  }

  getLineRequest(
    identifier: string,
    options?: RequestOptions,
  ): ApiRequestDetails | null {
    const url = this.lineUrl(identifier);
    return url ? this.buildRequest(url, options) : null;
  }

  getLineShapeRequest(
    href: string,
    options?: RequestOptions,
  ): ApiRequestDetails {
    return this.buildRequest(this.hrefUrl(href), options);
  }

  getLineAssetsRequest(
    href: string,
    options?: RequestOptions,
  ): ApiRequestDetails {
    return this.buildRequest(this.hrefUrl(href), options);
  }

  planJourneyRequest(
    params: JourneyPlanRequest,
    options?: RequestOptions,
  ): ApiRequestDetails {
    return this.buildRequest(this.journeyPlanUrl(params), options);
  }

  planJourneyByHrefRequest(
    href: string,
    options?: RequestOptions,
  ): ApiRequestDetails {
    return this.buildRequest(this.hrefUrl(href), options);
  }

  private mapJourneyPlanResponse(data: HalPlanResponse): JourneyPlanResponse {
    const plans: JourneyPlan[] = (data._embedded?.['plan:plan'] ?? []).map(
      (plan) => ({
        legs: (plan._embedded?.['plan:leg'] ?? []).map(mapLeg),
      }),
    );

    return {
      plans,
      laterPage: mapJourneyPlanPageLink(data._links?.later),
      earlierPage: mapJourneyPlanPageLink(data._links?.earlier),
    };
  }

  // --- Operators ---

  async listOperators(options?: RequestOptions): Promise<Operator[]> {
    const data = await this.fetch<HalOperatorsResponse>(
      this.listOperatorsRequest(options).url,
      options,
    );
    return (data._embedded?.['transmodel:operator'] ?? []).map((op) => ({
      code: op.code,
      name: op.name,
    }));
  }

  // --- Stops ---

  async searchStops(
    params: StopSearchParams | string,
    options?: RequestOptions,
  ): Promise<Stop[]> {
    const request = this.searchStopsRequest(params, options);

    if (!request) {
      return [];
    }

    const data = await this.fetch<HalStopsResponse>(request.url, options);
    return (data._embedded?.['naptan:stop'] ?? []).flatMap((stop) => {
      const mapped = mapStop(stop);
      return mapped ? [mapped] : [];
    });
  }

  async searchPlaces(
    params: PlaceSearchParams | string,
    options?: RequestOptions,
  ): Promise<PlaceSearchResult[]> {
    const request = this.searchPlacesRequest(params, options);

    if (!request) {
      return [];
    }

    const data = await this.fetch<HalPlacesResponse>(request.url, options);

    return (data._embedded?.place ?? []).flatMap((entry) => {
      const mapped = mapPlaceSearchResult(entry);
      return mapped ? [mapped] : [];
    });
  }

  async reverseGeocode(
    location: string,
    options?: RequestOptions,
  ): Promise<PlaceSearchResult | null> {
    const trimmed = location.trim();

    if (!LAT_LNG_PATTERN.test(trimmed)) {
      throw new Error(
        'reverseGeocode location must be a lat,lng coordinate string',
      );
    }

    const data = await this.fetch<HalPlacesResponse>(
      this.url('places', { location: trimmed }),
      options,
    );

    for (const entry of data._embedded?.place ?? []) {
      const mapped = mapPlaceSearchResult(entry);

      if (mapped) {
        return mapped;
      }
    }

    return null;
  }

  async getStop(
    atcoCode: string,
    options?: RequestOptions,
  ): Promise<StopDetail> {
    const data = await this.fetch<HalStopDetailResponse>(
      this.getStopRequest(atcoCode, options).url,
      options,
    );

    const mappedStop = mapStop(data);
    if (!mappedStop) {
      throw new Error('Invalid stop response from API.');
    }

    return {
      ...mappedStop,
      lines: mapLineRefs(data._links?.['transmodel:line']),
      streetViewLinks: mapHrefLinks(data._links?.streetview),
    };
  }

  // --- Departures ---
  async getStopDepartures(
    href: string,
    options?: RequestOptions,
  ): Promise<StopDepartures> {
    const data = await this.fetch<HalVisitsResponse>(
      this.getStopDeparturesRequest(href, options).url,
      options,
    );

    const stop = data._links?.['naptan:stop']
      ? (mapStop(data._links['naptan:stop']) ?? undefined)
      : undefined;

    const visits = (data._embedded?.['timetable:visit'] ?? []).flatMap((v) => {
      const mapped = mapVisit(v);
      return mapped ? [mapped] : [];
    });

    return {
      stop,
      visits,
      streetViewLinks: mapHrefLinks(data._links?.streetview),
    };
  }

  async getDisruptions(
    href: string,
    options?: RequestOptions,
  ): Promise<Disruption[]> {
    const data = await this.fetch<HalDisruptionsResponse>(
      this.getDisruptionsRequest(href, options).url,
      options,
    );

    return mapDisruptions(extractEmbeddedDisruptions(data._embedded));
  }

  // --- Journey Planning ---

  async planJourney(
    params: JourneyPlanRequest,
    options?: RequestOptions,
  ): Promise<JourneyPlanResponse> {
    const data = await this.fetch<HalPlanResponse>(
      this.planJourneyRequest(params, options).url,
      options,
    );
    return this.mapJourneyPlanResponse(data);
  }

  async planJourneyByHref(
    href: string,
    options?: RequestOptions,
  ): Promise<JourneyPlanResponse> {
    const data = await this.fetch<HalPlanResponse>(
      this.planJourneyByHrefRequest(href, options).url,
      options,
    );

    return this.mapJourneyPlanResponse(data);
  }

  // --- Lines ---

  async getOperatorLines(
    operator: string,
    options?: RequestOptions,
  ): Promise<Line[]> {
    const data = await this.fetch<HalLinesResponse>(
      this.getOperatorLinesRequest(operator, options).url,
      options,
    );
    return (data._embedded?.['transmodel:line'] ?? []).flatMap((line) => {
      const mapped = mapLine(line);
      return mapped ? [mapped] : [];
    });
  }

  async searchLines(query: string, options?: RequestOptions): Promise<Line[]> {
    const request = this.searchLinesRequest(query, options);

    if (!request) return [];

    const data = await this.fetch<HalLinesResponse>(request.url, options);
    return (data._embedded?.['transmodel:line'] ?? []).flatMap((line) => {
      const mapped = mapLine(line);
      return mapped ? [mapped] : [];
    });
  }

  async getLine(identifier: string, options?: RequestOptions): Promise<Line> {
    const request = this.getLineRequest(identifier, options);

    if (!request) {
      throw new Error('Line request is missing a line identifier.');
    }

    const data = await this.fetch<HalLine>(request.url, options);
    const mapped = mapLine(data);

    if (!mapped) {
      throw new Error('Invalid line response from API.');
    }

    return mapped;
  }

  // --- Timetables ---

  async getTimetable(
    href: string,
    options?: RequestOptions,
  ): Promise<Timetable> {
    const data = await this.fetch<HalTimetableResponse>(
      this.getTimetableRequest(href, options).url,
      options,
    );
    return mapTimetableResponse(data);
  }

  // --- Line Shapes ---

  async getLineShape(
    href: string,
    options?: RequestOptions,
  ): Promise<LineShape> {
    return this.fetch<LineShape>(
      this.getLineShapeRequest(href, options).url,
      options,
    );
  }

  // --- Line Assets ---

  async getLineAssets(
    href: string,
    options?: RequestOptions,
  ): Promise<LineAssets> {
    const data = await this.fetch<HalLineAssetsResponse>(
      this.getLineAssetsRequest(href, options).url,
      options,
    );
    return mapLineAssetsResponse(data, (assetHref) =>
      this.lineAssetHrefUrl(assetHref),
    );
  }

  // --- Line Stops ---

  async getLineStops(href: string, options?: RequestOptions): Promise<Stop[]> {
    const data = await this.fetch<HalStopsResponse>(
      this.buildRequest(this.hrefUrl(href), options).url,
      options,
    );
    return (data._embedded?.['naptan:stop'] ?? []).flatMap((stop) => {
      const mapped = mapStop(stop);
      return mapped ? [mapped] : [];
    });
  }
}

function mapJourneyPlanPageLink(
  link: HalPlanLink | undefined,
): JourneyPlanPageLink | undefined {
  if (!link?.href) {
    return undefined;
  }

  return {
    href: link.href,
    title: link.title,
    time: link.time,
  };
}
